import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  type ICognitoStorage,
} from 'amazon-cognito-identity-js';
import { AuthError, type AuthProvider, type SignInResult } from '../contract/auth.js';

/**
 * Cognito SRP provider. Passwords go from the form to Cognito's SRP exchange
 * and nowhere else — no GiftMotor API ever sees one.
 *
 * The library defaults to localStorage for its session cache; the B-AUTH
 * doctrine forbids ANY persistent credential storage, so every pool/user is
 * constructed with a throwaway in-memory storage.
 */

class MemoryStorage implements ICognitoStorage {
  private m = new Map<string, string>();
  setItem(key: string, value: string): void {
    this.m.set(key, value);
  }
  getItem(key: string): string | null {
    return this.m.get(key) ?? null;
  }
  removeItem(key: string): void {
    this.m.delete(key);
  }
  clear(): void {
    this.m.clear();
  }
}

function mapError(err: unknown): AuthError {
  const e = err as { code?: string; name?: string; message?: string };
  const name = e.code ?? e.name ?? '';
  const message = e.message ?? 'Something went wrong';
  switch (name) {
    case 'NotAuthorizedException':
      return new AuthError('INVALID_CREDENTIALS', 'Incorrect email or password.');
    case 'UserNotFoundException':
      // Same message as bad password: never confirm whether an account exists.
      return new AuthError('INVALID_CREDENTIALS', 'Incorrect email or password.');
    case 'UserNotConfirmedException':
      return new AuthError('USER_NOT_CONFIRMED', 'Confirm your email to continue.');
    case 'CodeMismatchException':
      return new AuthError('CODE_MISMATCH', 'That code is not right. Check and try again.');
    case 'ExpiredCodeException':
      return new AuthError('CODE_EXPIRED', 'That code has expired. Request a new one.');
    case 'InvalidPasswordException':
      return new AuthError(
        'PASSWORD_POLICY',
        'Passwords need at least 12 characters, with a lowercase letter and a number.',
      );
    case 'UsernameExistsException':
      return new AuthError('EMAIL_TAKEN', 'An account with this email already exists.');
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return new AuthError('RATE_LIMITED', 'Too many attempts. Wait a moment and try again.');
    case 'NetworkError':
      return new AuthError('NETWORK', 'Network problem. Check your connection and try again.');
    default:
      return new AuthError('UNKNOWN', message);
  }
}

export class CognitoAuthProvider implements AuthProvider {
  constructor(
    private readonly userPoolId: string,
    private readonly clientId: string,
  ) {}

  private pool(): CognitoUserPool {
    return new CognitoUserPool({
      UserPoolId: this.userPoolId,
      ClientId: this.clientId,
      Storage: new MemoryStorage(),
    });
  }

  private user(email: string): CognitoUser {
    return new CognitoUser({ Username: email, Pool: this.pool(), Storage: new MemoryStorage() });
  }

  signIn(email: string, password: string): Promise<SignInResult> {
    return new Promise((resolve, reject) => {
      this.user(email).authenticateUser(
        new AuthenticationDetails({ Username: email, Password: password }),
        {
          onSuccess: (session) => {
            const accessToken = session.getAccessToken();
            resolve({
              tokens: {
                accessToken: accessToken.getJwtToken(),
                idToken: session.getIdToken().getJwtToken(),
                expiresIn: Math.max(0, accessToken.getExpiration() - Math.floor(Date.now() / 1000)),
              },
              refreshToken: session.getRefreshToken().getToken(),
            });
          },
          onFailure: (err) => reject(mapError(err)),
          newPasswordRequired: () =>
            reject(
              new AuthError(
                'NEW_PASSWORD_REQUIRED',
                'This account needs a password reset. Use "Forgot password".',
              ),
            ),
        },
      );
    });
  }

  signUp(email: string, password: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.pool().signUp(
        email,
        password,
        [new CognitoUserAttribute({ Name: 'email', Value: email })],
        [],
        (err) => (err ? reject(mapError(err)) : resolve()),
      );
    });
  }

  confirmSignUp(email: string, code: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.user(email).confirmRegistration(code, true, (err) =>
        err ? reject(mapError(err)) : resolve(),
      );
    });
  }

  resendConfirmationCode(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.user(email).resendConfirmationCode((err) => (err ? reject(mapError(err)) : resolve()));
    });
  }

  forgotPassword(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.user(email).forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (err) => reject(mapError(err)),
        inputVerificationCode: () => resolve(),
      });
    });
  }

  confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.user(email).confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: (err) => reject(mapError(err)),
      });
    });
  }
}
